import { BottomSheet, Divider, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { Controller } from "react-hook-form";

import { useGetFavoriteWorkplaceEntitlement } from "../../FavoriteWorkplaces/useGetFavoriteWorkplaceEntitlement";
import { FavoriteWorkplacesFilter } from "../FavoriteWorkplacesFilter";
import { IncludedStatesFilterButtonGroup } from "../IncludedStatesFilterButtonGroup";
import { QualificationsFilterButtonGroup } from "../QualificationsFilterButtonGroup";
import { useShiftDiscoveryUserFiltersContext } from "../useUserFiltersContext";
import { AccordionCard } from "./AccordionCard";
import { AccordionGroup } from "./AccordionGroup";
import { calculateActiveFiltersCount } from "./calculateActiveFiltersCount";
import { DistanceFilter } from "./DistanceFilter";
import { FilterSectionSkeleton } from "./FilterSectionSkeleton";
import { FilterSectionWrapper } from "./FilterSectionWrapper";
import { FiltersHeader } from "./FiltersHeader";
import { FiltersInfoMessage } from "./FiltersInfoMessage";
import { getWhenFilterSummary } from "./filterSummaries";
import { FiltersBottomSheetFooter } from "./Footer";
import { getFiltersAppliedEventPayload } from "./getFiltersAppliedEventPayload";
import { ShowPriorityShiftsFilter } from "./ShowPriorityShiftsFilter";
import { useFilteredOpenShiftsCount } from "./useFilteredOpenShiftsCount";
import { useFilteredOpenShiftsStates } from "./useFilteredOpenShiftsStates";
import { useFiltersForm } from "./useFiltersForm";
import { WhenFilterComponent } from "./WhenFilterComponent";

interface ShiftDiscoveryFiltersBottomSheetV2Props {
  modalState: UseModalState;
}

export function ShiftDiscoveryFiltersBottomSheetV2(props: ShiftDiscoveryFiltersBottomSheetV2Props) {
  const { modalState } = props;

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const { form, resetFilters, cancelFilters, submitFilters } = useFiltersForm();

  const formValues = form.watch();

  const { count: filteredOpenShiftsCount, isLoading: filteredOpenShiftsIsLoading } =
    useFilteredOpenShiftsCount(formValues);
  const includeWindowedOutShiftsEnabled = useCbhFlag(CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS, {
    defaultValue: false,
  });

  const { isEntitlementEnabled: isFavoriteWorkplacesEnabled } =
    useGetFavoriteWorkplaceEntitlement();

  const { dates, clearDates } = useShiftDiscoveryUserFiltersContext();

  const {
    states: filteredOpenShiftsStates,
    hasShiftsInNonHomeStates,
    isLoading: filteredOpenShiftsStatesIsLoading,
  } = useFilteredOpenShiftsStates(formValues);
  const showIncludedStatesFilter =
    (isDefined(formValues.includedStates) && formValues.includedStates.length > 0) ||
    hasShiftsInNonHomeStates;

  const activeFiltersCount = calculateActiveFiltersCount(formValues, dates.length);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <FiltersBottomSheetFooter
          showClearFormButton={activeFiltersCount > 0}
          openShiftsCount={filteredOpenShiftsCount}
          openShiftsIsLoading={filteredOpenShiftsIsLoading}
          onClear={() => {
            logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_CLEARED, {
              trigger: "Filters Modal",
              shiftCount: filteredOpenShiftsCount,
            });
            clearDates();
            resetFilters();
          }}
          onCancel={() => {
            logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_CLOSED, {
              shiftCount: filteredOpenShiftsCount,
            });
            cancelFilters();
            modalState.closeModal();
          }}
          onSubmit={() => {
            logEvent(
              APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_APPLIED,
              getFiltersAppliedEventPayload({
                filteredOpenShiftsCount,
                distance: formValues.distance,
                shiftTimeSlots: formValues.shiftTimeSlots,
                licenses: formValues.licenses,
                workerQualifications,
                includeWindowedOutShiftsEnabled,
                includeWindowedOutShifts: formValues.includeWindowedOutShifts,
                isFavoriteWorkplacesEnabled,
                favoriteWorkplacesOnly: formValues.favoriteWorkplacesOnly,
                includedStates: formValues.includedStates,
              })
            );
            submitFilters();
            modalState.closeModal();
          }}
        />
      }
    >
      <DialogContent sx={{ paddingBottom: 12, paddingX: 5, paddingTop: 4 }}>
        <Stack spacing={6}>
          <FiltersHeader activeFiltersCount={activeFiltersCount} />

          {/* When filters in accordion */}
          <AccordionGroup>
            <AccordionCard
              label="When"
              subtitle="Dates and times"
              icon="calendar-clock"
              initialDescription={getWhenFilterSummary(formValues.shiftTimeSlots, dates)}
            >
              <WhenFilterComponent form={form} />
            </AccordionCard>
          </AccordionGroup>

          <Divider
            sx={(theme) => ({
              borderColor: theme.palette.secondary.dark,
              opacity: 1,
            })}
          />

          <FiltersInfoMessage />

          <Stack spacing={8} sx={{ paddingX: 2 }}>
            {/* Distance */}
            <Controller
              control={form.control}
              name="distance"
              render={({ field }) => (
                <DistanceFilter value={field.value} onChange={field.onChange} />
              )}
            />

            <Divider />

            {/* Licenses */}
            <Controller
              control={form.control}
              name="licenses"
              render={({ field }) => {
                return (
                  <FilterSectionWrapper sectionTitle="Shift Roles">
                    {workerQualificationsAreLoaded ? (
                      <QualificationsFilterButtonGroup
                        value={field.value}
                        qualifications={workerQualifications ?? []}
                        onChange={field.onChange}
                      />
                    ) : (
                      <FilterSectionSkeleton ariaLabel="Shift Licenses Filter Loading" />
                    )}
                  </FilterSectionWrapper>
                );
              }}
            />

            {isFavoriteWorkplacesEnabled && (
              <>
                <Divider />
                <Controller
                  control={form.control}
                  name="favoriteWorkplacesOnly"
                  render={({ field }) => (
                    <FilterSectionWrapper sectionTitle="Workplaces">
                      <FavoriteWorkplacesFilter value={field.value} onChange={field.onChange} />
                    </FilterSectionWrapper>
                  )}
                />
              </>
            )}

            {includeWindowedOutShiftsEnabled && (
              <>
                <Divider />
                <Controller
                  control={form.control}
                  name="includeWindowedOutShifts"
                  render={({ field }) => (
                    <ShowPriorityShiftsFilter checked={field.value} onChange={field.onChange} />
                  )}
                />
              </>
            )}

            {showIncludedStatesFilter && (
              <>
                <Divider />
                <Controller
                  control={form.control}
                  name="includedStates"
                  render={({ field }) => (
                    <FilterSectionWrapper
                      sectionTitle="States"
                      sectionSubtitle="Show shifts in specific states"
                    >
                      {filteredOpenShiftsStatesIsLoading ? (
                        <FilterSectionSkeleton ariaLabel="States Filter Loading" />
                      ) : (
                        <IncludedStatesFilterButtonGroup
                          value={field.value}
                          availableStates={[...filteredOpenShiftsStates].sort()}
                          onChange={field.onChange}
                        />
                      )}
                    </FilterSectionWrapper>
                  )}
                />
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
